import React, { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import { BiEdit } from "react-icons/bi";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import NeedToGetInTouch from "../components/ui/NeedToGetInTouch";
import api from "../api/axios";
import PageImageSelector from "../components/ui/PageImageSelector";
import LoadingSpinner from "../components/LoadingSpinner";
import heroImg from "../assets/aboutus/about-us-hero-bg.jpg";
import sideCars from "../assets/aboutus/side-cars-new.png";
import { HiMiniCheckBadge } from "react-icons/hi2";
import whatDrivesUsCarImg from "../assets/aboutus/aboutus1.png";
import driveChange from "../assets/aboutus/driving-change-together.jpg";

export default function AboutUs() {
  const { user } = useAuth();
  const isAdmin = user?.roles.includes("super-admin");

  const [pageContent, setPageContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Tracks which “section key” is currently in edit mode
  const [editingSection, setEditingSection] = useState(null);

  // Refs for hidden file inputs
  const heroFileInputRef = useRef(null);
  const ourPeopleFileInputRef = useRef(null);

  useEffect(() => {
    api
      .get("/pages/about-us")
      .then((res) => {
        setPageContent(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setError("Cannot load page content");
        setLoading(false);
      });
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return <p className="text-red-600">{error}</p>;
  if (!pageContent) return <p>No content</p>;

  async function saveContent(updatedContent) {
    try {
      await api.put("/pages/about-us", { content: updatedContent });
      setPageContent(updatedContent);
      toast.success("Saved successfully");
      setEditingSection(null);
    } catch (e) {
      console.error(e);
      toast.error("Failed to save");
    }
  }

  // Helper to upload a single page image under “about-us” prefix
  async function uploadPageImage(file, onUrlReceived) {
    const form = new FormData();
    form.append("pageKey", "about-us");
    form.append("images", file);

    try {
      const res = await api.post("/page-images", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const newImg = res.data[0];
      onUrlReceived(newImg.url);
    } catch (err) {
      console.error("Upload failed:", err);
      toast.error("Image upload failed");
    }
  }

  // ────────────────────────────────────────────────────────────────────────────
  // SECTIONS
  // ────────────────────────────────────────────────────────────────────────────

  // 1) HERO SECTION
  const hero = pageContent.hero || { title: "", subtitle: "", imageUrl: "" };

  // 2) “What drives us”
  const drivesSection = pageContent.sections.find(
    (s) => s.key === "what-drives-us"
  ) || {
    key: "what-drives-us",
    heading: "",
    copy: "",
  };

  // 3) “Our people”
  const peopleSection = pageContent.sections.find(
    (s) => s.key === "our-people"
  ) || {
    key: "our-people",
    heading: "",
    copy: "",
    imageUrl: "",
    cta: { text: "", href: "" },
  };

  return (
    <div className="w-full">
      {/* HERO SECTION */}
      <section className="relative w-full h-[70vh] md:h-[90vh] flex items-end text-white overflow-hidden">
        {/* Background image */}
        <img
          src={heroImg || hero.imageUrl}
          alt="Hero background"
          className="absolute inset-0 w-full h-full object-cover z-0"
        />

        {/* FULL OVERLAY tint instead of gradient */}
        <div
          className="absolute inset-0 z-10"
          style={{
            background:
              "linear-gradient(rgba(31, 41, 84, 0.5) 10%, rgba(239, 81, 75, 0.6) 77%)",
          }}
        />

        {/* Text content */}
        <div className="relative z-20 p-6 md:p-20 mb-20">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            Welcome to ZORQK
          </h1>
          <p className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold">
            The future of motoring platforms
          </p>
        </div>
      </section>

      {/* ABOUT US SECTION (OVERLAPPING HERO) */}
      <section className="relative -mt-20 bg-[#1c2553] text-white rounded-t-3xl md:rounded-t-[50px] z-30">
        <div className="w-full relative z-10">
          {editingSection === "what-drives-us" ? (
            <>
              <input
                type="text"
                className="text-black w-full mb-4 px-4 py-2 text-2xl font-bold rounded"
                value={drivesSection.heading}
                onChange={(e) => {
                  setPageContent((prev) => ({
                    ...prev,
                    sections: prev.sections.map((sec) =>
                      sec.key === "what-drives-us"
                        ? { ...sec, heading: e.target.value }
                        : sec
                    ),
                  }));
                }}
              />
              <textarea
                className="text-black w-full mb-4 px-4 py-2 rounded"
                rows={5}
                value={drivesSection.copy}
                onChange={(e) => {
                  setPageContent((prev) => ({
                    ...prev,
                    sections: prev.sections.map((sec) =>
                      sec.key === "what-drives-us"
                        ? { ...sec, copy: e.target.value }
                        : sec
                    ),
                  }));
                }}
              />
              <div className="flex space-x-2">
                <button
                  className="px-4 py-2 bg-green-600 text-white rounded"
                  onClick={() => saveContent({ ...pageContent })}
                >
                  Save
                </button>
                <button
                  className="px-4 py-2 bg-gray-600 text-white rounded"
                  onClick={() => setEditingSection(null)}
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <div className="py-10 md:pt-32">
              {/* Floating cars */}
              <div className="hidden md:block absolute bottom-0 right-0 z-0">
                <img
                  src={sideCars}
                  alt="Cars"
                  className="w-[35vw] mb-60 lg:mb-0 object-contain"
                />
              </div>
              <div className="pl-8 pr-8 md:pr-0 md:pl-32 max-w-3xl">
                <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl text-accent font-bold mb-6">
                  {"About Us" || drivesSection.heading}
                </h2>
                <p className="text-base md:text-lg lg:text-xl text-white/90 max-w-3xl">
                  {drivesSection.copy}
                </p>

                {/* Satisfaction icon */}
                <div className="mt-10 flex items-center space-x-4">
                  <HiMiniCheckBadge className="w-16 h-16 text-white bg-accent rounded-full p-2 object-contain mb-4" />
                  <p className="text-lg font-semibold">
                    Satisfaction Guaranteed
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ─── “What drives us” SECTION ──────────────────────────────────────────────── */}
      <section className="bg-white py-10 px-4 rounded-tl-3xl relative">
        {isAdmin && editingSection !== "what-drives-us" && (
          <BiEdit
            onClick={() => setEditingSection("what-drives-us")}
            className="absolute top-4 right-4 text-xl text-brandAccent cursor-pointer hover:text-accent"
            title="Edit “What drives us”"
          />
        )}

        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 ">
          {/* Left: Image */}
          <div>
            <img
              src={whatDrivesUsCarImg} // Replace with drivesSection.imageUrl if dynamic
              alt="What drives us"
              className="w-full h-auto rounded-3xl object-cover"
            />
          </div>

          {/* Right: Text Content */}
          <div>
            {editingSection === "what-drives-us" ? (
              <>
                <input
                  type="text"
                  className="text-primary w-full mb-4 px-4 py-2 text-2xl font-bold rounded"
                  value={drivesSection.heading}
                  onChange={(e) => {
                    setPageContent((prev) => ({
                      ...prev,
                      sections: prev.sections.map((sec) =>
                        sec.key === "what-drives-us"
                          ? { ...sec, heading: e.target.value }
                          : sec
                      ),
                    }));
                  }}
                />
                <textarea
                  className="text-primary w-full mb-4 px-4 py-2 rounded"
                  rows={5}
                  value={drivesSection.copy}
                  onChange={(e) => {
                    setPageContent((prev) => ({
                      ...prev,
                      sections: prev.sections.map((sec) =>
                        sec.key === "what-drives-us"
                          ? { ...sec, copy: e.target.value }
                          : sec
                      ),
                    }));
                  }}
                />
                <div className="flex space-x-2">
                  <button
                    className="px-4 py-2 bg-green-600 text-white rounded"
                    onClick={() => saveContent({ ...pageContent })}
                  >
                    Save
                  </button>
                  <button
                    className="px-4 py-2 bg-gray-600 text-white rounded"
                    onClick={() => setEditingSection(null)}
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <div className="text-right">
                <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl text-accent font-bold mb-5">
                  {"What drives us" || drivesSection.heading}
                </h2>
                <p className="text-base md:text-lg lg:text-xl text-primary font-semibold mb-4">
                  We have a huge range of cars, from people carriers to head
                  turners. You can part exchange, apply for finance or pay in
                  full – and choose between home delivery or collection.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ─── “Our people” SECTION ─────────────────────────────────────────────────── */}
      <section className="bg-white relative overflow-hidden pb-10 md:pb-60">
        {isAdmin && editingSection !== "our-people" && (
          <BiEdit
            onClick={() => setEditingSection("our-people")}
            className="absolute top-4 right-4 text-xl text-brandAccent cursor-pointer hover:text-accent"
            title="Edit “Our people”"
          />
        )}

        <div className="max-w-4xl px-5 md:px-20">
          {/* Text Content */}
          <div className="space-y-6">
            {editingSection === "our-people" ? (
              <>
                <input
                  type="text"
                  className="text-primary w-full mb-2 px-3 py-2 text-2xl font-bold rounded"
                  value={peopleSection.heading}
                  onChange={(e) => {
                    setPageContent((prev) => ({
                      ...prev,
                      sections: prev.sections.map((sec) =>
                        sec.key === "our-people"
                          ? { ...sec, heading: e.target.value }
                          : sec
                      ),
                    }));
                  }}
                />
                <textarea
                  className="text-primary w-full mb-2 px-3 py-2 rounded"
                  rows={6}
                  value={peopleSection.copy}
                  onChange={(e) => {
                    setPageContent((prev) => ({
                      ...prev,
                      sections: prev.sections.map((sec) =>
                        sec.key === "our-people"
                          ? { ...sec, copy: e.target.value }
                          : sec
                      ),
                    }));
                  }}
                />

                {/* Image selector */}
                <div className="mb-2">
                  <label className="block mb-1 text-sm font-medium text-primary">
                    Our People Image
                  </label>
                  <PageImageSelector
                    pageKey="about-us"
                    value={peopleSection.imageUrl}
                    onChange={(newUrl) => {
                      if (newUrl === "__upload_new__") {
                        ourPeopleFileInputRef.current.click();
                      } else {
                        setPageContent((prev) => ({
                          ...prev,
                          sections: prev.sections.map((sec) =>
                            sec.key === "our-people"
                              ? { ...sec, imageUrl: newUrl }
                              : sec
                          ),
                        }));
                      }
                    }}
                  />
                  <input
                    type="file"
                    accept="image/*"
                    ref={ourPeopleFileInputRef}
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      uploadPageImage(file, (url) => {
                        setPageContent((prev) => ({
                          ...prev,
                          sections: prev.sections.map((sec) =>
                            sec.key === "our-people"
                              ? { ...sec, imageUrl: url }
                              : sec
                          ),
                        }));
                      });
                      e.target.value = null;
                    }}
                  />
                </div>

                {/* CTA */}
                <input
                  type="text"
                  className="text-primary w-full mb-2 px-3 py-2 rounded"
                  placeholder="CTA Text"
                  value={peopleSection.cta.text}
                  onChange={(e) => {
                    setPageContent((prev) => ({
                      ...prev,
                      sections: prev.sections.map((sec) =>
                        sec.key === "our-people"
                          ? {
                              ...sec,
                              cta: { ...sec.cta, text: e.target.value },
                            }
                          : sec
                      ),
                    }));
                  }}
                />
                <input
                  type="text"
                  className="text-primary w-full mb-2 px-3 py-2 rounded"
                  placeholder="CTA Href"
                  value={peopleSection.cta.href}
                  onChange={(e) => {
                    setPageContent((prev) => ({
                      ...prev,
                      sections: prev.sections.map((sec) =>
                        sec.key === "our-people"
                          ? {
                              ...sec,
                              cta: { ...sec.cta, href: e.target.value },
                            }
                          : sec
                      ),
                    }));
                  }}
                />
                <div className="flex space-x-2">
                  <button
                    className="px-4 py-2 bg-green-600 text-white rounded"
                    onClick={() => saveContent({ ...pageContent })}
                  >
                    Save
                  </button>
                  <button
                    className="px-4 py-2 bg-gray-600 text-white rounded"
                    onClick={() => setEditingSection(null)}
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <div className="flex flex-col gap-5">
                <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl text-accent font-bold pt-5">
                  {peopleSection.heading || "Our people"}
                </h2>
                <p className="text-base md:text-lg lg:text-xl font-semibold text-[#1c2553] md:mb-28">
                  We employ experts in their field for every stage of the
                  journey, from car selection to inspection, preparation, and
                  delivery, including our UK-based customer service team who are
                  available 7 days a week.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ─── “Need to get in touch?” SECTION ─────────────────────────────────────── */}
      <NeedToGetInTouch
        section={pageContent.sections.find(
          (s) => s.key === "need-to-get-in-touch"
        )}
        pageContent={pageContent}
        setPageContent={setPageContent}
        editingSection={editingSection}
        setEditingSection={setEditingSection}
        isAdmin={isAdmin}
        saveContent={saveContent}
      />

      {/* ─── “Driving change together” SECTION ──────────────────────────────────────────────── */}
      <section className="bg-white py-10 px-4 md:px-10 rounded-tl-3xl relative">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 ">
          {/* Left: Image */}
          <div>
            <img
              src={driveChange} // Replace with drivesSection.imageUrl if dynamic
              alt="What drives us"
              className="w-full h-[500px] rounded-3xl object-cover"
            />
          </div>

          {/* Right: Text Content */}
          <div className="flex flex-col justify-center items-center">
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl text-primary font-bold mb-5">
              Driving change together responsibly
            </h2>
            <p className="text-sm md:text-base lg:text-lg text-primary font-semibold mb-4">
              Core to our business strategy, we deliver our purpose via a clear
              focus on our three strategic priorities, alongside our commitment
              to doing business responsibly.
            </p>
            <p className="text-sm md:text-base lg:text-lg text-secondary leading-relaxed mb-4">
              We are committed to being a responsible business; driven by our
              resolve to do the right thing, measure and report transparently
              and always act ethically, and with integrity.
            </p>
            <p className="text-sm md:text-base lg:text-lg text-secondary leading-relaxed">
              Our diverse and inclusive culture simply wouldn’t be possible
              without our steadfast commitment to our purpose. Pulling together,
              we all work towards the same goal.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
